import React from "react";
import { Link, NavLink } from "react-router-dom";
import { useI18n, LANGS } from "@/i18n/I18nContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Menu, X, Anchor } from "lucide-react";

export default function Header() {
  const { t, lang, setLang, isRTL } = useI18n();
  const [open, setOpen] = React.useState(false);

  const items = [
    { to: "/", key: "nav_home" },
    { to: "/history", key: "nav_history" },
    { to: "/sights", key: "nav_sights" },
    { to: "/beaches", key: "nav_beaches" },
    { to: "/mountains", key: "nav_mountains" },
    { to: "/directory", key: "nav_directory" },
    { to: "/tips", key: "nav_tips" },
    { to: "/gallery", key: "nav_gallery" },
    { to: "/map", key: "nav_map" },
  ];

  return (
    <header className="glass sticky top-0 z-50 border-b border-stone-200/70" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[var(--brand-primary)] text-white">
            <Anchor className="h-4 w-4" />
          </span>
          <span className="font-display text-xl tracking-tight">Al Quseir</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="primary-nav">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) =>
                `text-sm transition-colors ${isActive ? "text-[var(--brand-primary)]" : "text-stone-700 hover:text-[var(--brand-primary)]"}`
              }
              data-testid={`nav-${it.key}`}
            >
              {t(it.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="h-9 w-[120px] rounded-full border-stone-300 bg-white/70" data-testid="language-switcher">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGS.map((l) => (
                <SelectItem key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>
                  <span className="me-2 inline-block w-6 text-xs font-bold text-stone-500">{l.flag}</span>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Link
            to="/advertise"
            className="hidden md:inline-flex items-center rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white text-sm font-medium ps-5 pe-5 h-9 transition"
            data-testid="advertise-btn"
          >
            {t("nav_advertise")}
          </Link>

          <button
            className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-full border border-stone-300"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-stone-200 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 grid grid-cols-2 gap-3">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={() => setOpen(false)}
                className="text-sm py-1.5 text-stone-700"
                data-testid={`mnav-${it.key}`}
              >
                {t(it.key)}
              </NavLink>
            ))}
            <NavLink to="/advertise" onClick={() => setOpen(false)} className="text-sm py-1.5 font-medium text-[var(--brand-primary)]">
              {t("nav_advertise")}
            </NavLink>
          </div>
        </div>
      )}
    </header>
  );
}
